import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { taskService } from '../services/api';

const UserDashboard = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        // En una implementación real, estos datos vendrían del backend
        // Aquí simulamos datos para la demo
        
        // Obtener tareas recientes
        const tasksResponse = await taskService.getTasks({ 
          limit: 3, 
          sortBy: 'createdAt', 
          sortOrder: 'desc' 
        });
        
        setRecentTasks(tasksResponse.data.tasks || []);
        
        // Simular estadísticas
        setStats({
          tasksCreated: 12,
          tasksCompleted: 8,
          tokensEarned: currentUser?.tokensBlue || 80,
          tokensMinted: currentUser?.tokensRed || 120
        });
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando dashboard...</p>
      </div>
    );
  }
  
  return (
    <div className="dashboard-container">
      <h1 className="mb-4">Dashboard</h1>
      
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Bienvenido, {currentUser?.name}</h5>
              <p className="text-muted">Aquí tienes un resumen de tu actividad en la plataforma.</p>
              
              <div className="mt-4">
                <Link to="/tasks" className="btn btn-primary me-2">Ver Tareas</Link>
                <Link to="/tasks/create" className="btn btn-outline-primary">Crear Tarea</Link>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card wallet-card">
            <div className="card-body">
              <h5 className="card-title">Tu Billetera</h5>
              <div className="row mt-3">
                <div className="col-6">
                  <div className="token-balance blue">
                    <h3>{stats.tokensEarned}</h3>
                    <p>BLUE Tokens</p>
                  </div>
                </div>
                <div className="col-6">
                  <div className="token-balance red">
                    <h3>{stats.tokensMinted}</h3>
                    <p>RED Tokens</p>
                  </div>
                </div>
              </div>
              {!currentUser?.walletAddress && (
                <div className="alert alert-warning mt-3 mb-0">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  No has configurado tu dirección de wallet.
                  <Link to="/profile" className="alert-link ms-2">Configurar ahora</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center stat-card">
            <div className="card-body">
              <h5 className="card-title">{stats.tasksCreated}</h5>
              <p className="card-text">Tareas Creadas</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center stat-card">
            <div className="card-body">
              <h5 className="card-title">{stats.tasksCompleted}</h5>
              <p className="card-text">Tareas Completadas</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center stat-card">
            <div className="card-body">
              <h5 className="card-title">{stats.tokensEarned}</h5>
              <p className="card-text">BLUE Ganados</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center stat-card">
            <div className="card-body">
              <h5 className="card-title">{stats.tokensMinted}</h5>
              <p className="card-text">RED Minteados</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Tareas Recientes</h5>
            </div>
            <div className="list-group list-group-flush">
              {recentTasks.length === 0 ? (
                <div className="list-group-item text-center py-4">
                  <p className="mb-0">No hay tareas recientes.</p>
                </div>
              ) : (
                recentTasks.map(task => (
                  <Link 
                    key={task.id} 
                    to={`/tasks/${task.id}`}
                    className="list-group-item list-group-item-action"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">{task.title}</h6>
                        <small className="text-muted">
                          {new Date(task.createdAt).toLocaleDateString()}
                        </small>
                      </div>
                      <span className="badge bg-primary">{task.tokens} BLUE</span>
                    </div>
                  </Link>
                ))
              )}
            </div>
            <div className="card-footer text-center">
              <Link to="/tasks" className="btn btn-sm btn-outline-primary">
                Ver todas las tareas
              </Link>
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Enlaces Rápidos</h5>
            </div>
            <div className="list-group list-group-flush">
              <Link to="/profile" className="list-group-item list-group-item-action">
                <i className="fas fa-user me-2"></i> Mi Perfil
              </Link>
              <Link to="/my-tasks" className="list-group-item list-group-item-action">
                <i className="fas fa-tasks me-2"></i> Mis Tareas
              </Link>
              <Link to="/tasks/create" className="list-group-item list-group-item-action">
                <i className="fas fa-plus me-2"></i> Crear Tarea
              </Link>
              <Link to="/wallet" className="list-group-item list-group-item-action">
                <i className="fas fa-wallet me-2"></i> Mi Billetera
              </Link>
              <Link to="/messages" className="list-group-item list-group-item-action">
                <i className="fas fa-comment me-2"></i> Mensajes
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
